import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type SearchResultType = 'session' | 'scenario' | 'competition' | 'job' | 'course';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  path: string;
}

const RESULTS_PER_GROUP = 5;

// PostgREST `or=(...)` uses commas and parens as syntax, so they can't appear
// in a user-supplied filter value. `%` and `_` are ilike wildcards.
const sanitize = (q: string) => q.replace(/[,()%\\_*]/g, ' ').trim();

function useDebounced<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Searches the user's dashboard content (practice history, scenarios,
 * competitions, jobs, academy courses) in a single debounced pass.
 * RLS scopes every table to what the user is allowed to see.
 */
export function useDashboardSearch(query: string) {
  const { user } = useAuth();
  const debouncedQuery = useDebounced(query.trim(), 200);
  const term = sanitize(debouncedQuery);
  const enabled = !!user && term.length >= 2;

  const { data, isFetching } = useQuery({
    queryKey: ['dashboard-search', user?.id, term],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<SearchResult[]> => {
      const like = `%${term}%`;

      const [sessions, scenarios, competitions, jobs, courses] = await Promise.all([
        supabase
          .from('practice_sessions')
          .select('id, scenario_name, overall_score, started_at')
          .eq('user_id', user!.id)
          .ilike('scenario_name', like)
          .order('started_at', { ascending: false })
          .limit(RESULTS_PER_GROUP),
        supabase
          .from('custom_scenarios')
          .select('id, name, company, product_name, buyer_role')
          .or(`name.ilike.${like},company.ilike.${like},product_name.ilike.${like}`)
          .limit(RESULTS_PER_GROUP),
        supabase
          .from('competitions')
          .select('id, name, case_type, status, date_time')
          .ilike('name', like)
          .order('date_time', { ascending: false })
          .limit(RESULTS_PER_GROUP),
        supabase
          .from('job_postings')
          .select('id, title, company, location')
          .eq('is_active', true)
          .or(`title.ilike.${like},company.ilike.${like}`)
          .limit(RESULTS_PER_GROUP),
        supabase
          .from('academy_courses')
          .select('id, title, description')
          .eq('is_active', true)
          .or(`title.ilike.${like},description.ilike.${like}`)
          .limit(RESULTS_PER_GROUP),
      ]);

      const results: SearchResult[] = [];

      for (const s of sessions.data ?? []) {
        results.push({
          id: s.id,
          type: 'session',
          title: s.scenario_name,
          subtitle: [
            s.overall_score != null ? `Score ${s.overall_score}` : null,
            s.started_at ? new Date(s.started_at).toLocaleDateString() : null,
          ].filter(Boolean).join(' · '),
          path: `/sessions/${s.id}`,
        });
      }

      for (const s of scenarios.data ?? []) {
        results.push({
          id: s.id,
          type: 'scenario',
          title: s.name,
          subtitle: [s.company, s.product_name, s.buyer_role].filter(Boolean).join(' · '),
          path: `/practice/${s.id}`,
        });
      }

      for (const c of competitions.data ?? []) {
        results.push({
          id: c.id,
          type: 'competition',
          title: c.name,
          subtitle: [c.case_type, c.status].filter(Boolean).join(' · '),
          path: `/competitions/${c.id}`,
        });
      }

      for (const j of jobs.data ?? []) {
        results.push({
          id: j.id,
          type: 'job',
          title: j.title,
          subtitle: [j.company, j.location].filter(Boolean).join(' · '),
          path: `/jobs/${j.id}`,
        });
      }

      for (const c of courses.data ?? []) {
        results.push({
          id: c.id,
          type: 'course',
          title: c.title,
          subtitle: c.description?.slice(0, 80) ?? undefined,
          path: '/dashboard?tab=tutorials',
        });
      }

      return results;
    },
  });

  return {
    results: data ?? [],
    // Treat the un-debounced gap as loading so the empty state doesn't flash.
    isSearching: enabled && (isFetching || debouncedQuery !== query.trim()),
    isActive: enabled,
  };
}
